<script setup lang="ts">
import { ref, computed, watch, useTemplateRef } from 'vue';
import { z } from 'zod';
import { UiFormBuilder, UiButton, UiText, UrgencyFormOptions } from '@shopexperts/shared';
import type { BuilderRowConfig, Urgency } from '@shopexperts/shared';
import type { AdminCreateDraftLeadRequest } from '@shopexperts/shared';
import type { DraftLeadAdminResponse } from '../types';
import { useCreateDraftLead } from '../composables/useCreateDraftLead';
import { useUpdateDraftLead } from '../composables/useUpdateDraftLead';
import { useSubmitDraftLead } from '../composables/useSubmitDraftLead';
import { formatTimezoneWithOffset } from '../utils/timezone';

interface DraftLeadFormProps {
    /** If provided, form is in edit mode */
    draftLead?: DraftLeadAdminResponse | null;
}

const props = withDefaults(defineProps<DraftLeadFormProps>(), {
    draftLead: null,
});

const emit = defineEmits<{
    (e: 'success'): void;
    (e: 'cancel'): void;
}>();

// Form mode
const isEditMode = computed(() => !!props.draftLead);

// Mutations
const { mutateAsync: createDraft, isPending: isCreating } = useCreateDraftLead();
const { mutateAsync: updateDraft, isPending: isUpdating } = useUpdateDraftLead();
const { mutateAsync: submitDraft, isPending: isSubmitting } = useSubmitDraftLead();

const isLoading = computed(() => isCreating.value || isUpdating.value || isSubmitting.value);
const budgetLabel = computed(() => props.draftLead?.project_value_range?.label || 'Flexible');
const timezoneLabel = computed(() => formatTimezoneWithOffset(props.draftLead?.timezone));

// Form schema
const schema = z.object({
    email: z.string().email('Valid email is required'),
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    project_title: z.string().max(255, 'Project title must be 255 characters or less').optional(),
    project_description: z.string().optional(),
    store_url: z.string().url('Must be a valid URL').optional().or(z.literal('')),
    no_store_yet: z.boolean().optional(),
    brand_name: z.string().optional(),
    urgency: z.string().optional(),
    matching_strategy: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// Form model
const formModel = ref<FormValues>({
    email: '',
    first_name: '',
    last_name: '',
    project_title: '',
    project_description: '',
    store_url: '',
    no_store_yet: false,
    brand_name: '',
    urgency: '',
    matching_strategy: '',
});

// Generate a placeholder store URL when user doesn't have a store
function generatePlaceholderStoreUrl(): string {
    const randomNum = Math.floor(Math.random() * 900000) + 100000;
    return `https://new-store-${randomNum}.myshopify.com`;
}

// Track the user's actual store URL before checking "no store"
const userStoreUrlBeforeNoStore = ref<string>('');
const noStorePlaceholder = ref<string>('');

// Watch no_store_yet to manage the store_url field
watch(() => formModel.value.no_store_yet, (noStore, wasNoStore) => {
    if (noStore && !wasNoStore) {
        // User just checked "no store" - save current value and set placeholder
        userStoreUrlBeforeNoStore.value = formModel.value.store_url || '';
        if (!noStorePlaceholder.value) {
            noStorePlaceholder.value = generatePlaceholderStoreUrl();
        }
        formModel.value.store_url = noStorePlaceholder.value;
    } else if (!noStore && wasNoStore) {
        // User just unchecked "no store" - restore previous value
        formModel.value.store_url = userStoreUrlBeforeNoStore.value;
        userStoreUrlBeforeNoStore.value = '';
    }
});

const formBuilderRef = useTemplateRef('formBuilderRef');

// Form rows configuration
const rows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            { 
                type: 'email', 
                name: 'email', 
                label: 'Email', 
                props: { placeholder: 'client@example.com' } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'text', 
                name: 'first_name', 
                label: 'First Name', 
                props: { placeholder: 'John' } 
            },
            { 
                type: 'text', 
                name: 'last_name', 
                label: 'Last Name', 
                props: { placeholder: 'Doe' } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'text', 
                name: 'project_title', 
                label: 'Project Title', 
                props: { placeholder: 'E.g., Shopify store redesign' } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'textarea', 
                name: 'project_description', 
                label: 'Project Description', 
                props: { placeholder: 'Describe the project requirements...', rows: 4 } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'url', 
                name: 'store_url', 
                label: 'Store URL', 
                props: { placeholder: 'https://mystore.myshopify.com' } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'checkbox', 
                name: 'no_store_yet', 
                label: "Client doesn't have a Shopify store yet",
                props: {
                    label: "Client doesn't have a Shopify store yet",
                },
            },
        ]
    },
    {
        columns: [
            { 
                type: 'text', 
                name: 'brand_name', 
                label: 'Brand Name', 
                props: { placeholder: 'Company name' } 
            },
        ]
    },
    {
        columns: [
            { 
                type: 'select', 
                name: 'urgency', 
                label: 'Project Timeline',
                props: {
                    options: [
                        { value: '', label: 'Select timeline...' },
                        ...UrgencyFormOptions,
                    ]
                }
            },
            { 
                type: 'select', 
                name: 'matching_strategy', 
                label: 'Matching Strategy',
                props: {
                    options: [
                        { value: '', label: 'Select matching strategy...' },
                        { value: 'auto', label: 'Auto - Let us find the best expert' },
                        { value: 'selected', label: 'Selected - Client chose a specific expert' },
                        { value: 'selected-auto', label: 'Selected + Auto - Client preference with alternatives' },
                    ]
                }
            },
        ]
    },
]);

// Check if a store URL looks like a placeholder (new-store-XXXXXX.myshopify.com)
function isPlaceholderStoreUrl(url: string): boolean {
    return /new-store-\d+\.myshopify\.com/i.test(url);
}

// Initialize form when editing
watch(() => props.draftLead, (draft) => {
    if (draft) {
        const storeUrl = draft.store_url || '';
        const isNoStore = isPlaceholderStoreUrl(storeUrl);
        
        formModel.value = {
            email: draft.email || '',
            first_name: draft.first_name || '',
            last_name: draft.last_name || '',
            project_title: draft.project_title || '',
            project_description: draft.project_description || '',
            store_url: storeUrl,
            no_store_yet: isNoStore,
            brand_name: draft.brand_name || '',
            urgency: draft.urgency || '',
            matching_strategy: draft.matching_strategy || '',
        };
        
        // If it's a placeholder, store it so unchecking restores empty
        if (isNoStore) {
            noStorePlaceholder.value = storeUrl;
            userStoreUrlBeforeNoStore.value = '';
        }
    } else {
        resetForm();
    }
}, { immediate: true });

function resetForm() {
    formModel.value = {
        email: '',
        first_name: '',
        last_name: '',
        project_title: '',
        project_description: '',
        store_url: '',
        no_store_yet: false,
        brand_name: '',
        urgency: '',
        matching_strategy: '',
    };
    userStoreUrlBeforeNoStore.value = '';
    noStorePlaceholder.value = '';
    formBuilderRef.value?.resetForm();
}

function buildPayload(values: FormValues): AdminCreateDraftLeadRequest {
    const payload: Record<string, any> = {};

    // Only include non-empty values
    if (values.email) payload.email = values.email;
    if (values.first_name) payload.first_name = values.first_name;
    if (values.last_name) payload.last_name = values.last_name;
    if (values.project_title) payload.project_title = values.project_title;
    if (values.project_description) payload.project_description = values.project_description;
    if (values.store_url) payload.store_url = values.store_url;
    if (values.brand_name) payload.brand_name = values.brand_name;
    if (values.urgency) payload.urgency = values.urgency as Urgency;
    if (values.matching_strategy) payload.matching_strategy = values.matching_strategy as 'selected' | 'selected-auto' | 'auto';

    return payload as AdminCreateDraftLeadRequest;
}

async function handleSubmit(values: FormValues) {
    const payload = buildPayload(values);

    try {
        if (isEditMode.value && props.draftLead) {
            await updateDraft({ id: props.draftLead.id, data: payload });
        } else {
            await createDraft(payload);
        }
        emit('success');
    } catch (error) {
        console.error('[DraftLeadForm] Submit error:', error);
    }
}

async function handleSubmitAndCreate() {
    // Validate first
    const isValid = await formBuilderRef.value?.validate(true);
    if (!isValid) return;

    const payload = buildPayload(formModel.value);

    try {
        let draftId: number;
        
        if (isEditMode.value && props.draftLead) {
            await updateDraft({ id: props.draftLead.id, data: payload });
            draftId = props.draftLead.id;
        } else {
            const created = await createDraft(payload);
            draftId = created.id;
        }

        // Now submit the draft
        await submitDraft(draftId);
        emit('success');
    } catch (error) {
        console.error('[DraftLeadForm] Submit and create error:', error);
    }
}

function handleCancel() {
    resetForm();
    emit('cancel');
}

defineExpose({
    resetForm,
});
</script>

<template>
    <div
        v-if="draftLead"
        class="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 sm:grid-cols-2"
    >
        <div>
            <UiText as="p" variant="caption-normal" class="text-gray-500">
                Budget
            </UiText>
            <UiText as="p" variant="p-small" class="font-semibold text-gray-900">
                {{ budgetLabel }}
            </UiText>
        </div>
        <div>
            <UiText as="p" variant="caption-normal" class="text-gray-500">
                Timezone
            </UiText>
            <UiText as="p" variant="p-small" class="font-semibold text-gray-900">
                {{ timezoneLabel }}
            </UiText>
        </div>
    </div>
    <UiFormBuilder 
        ref="formBuilderRef"
        v-model="formModel" 
        :schema="schema" 
        :rows="rows"
        @submit="handleSubmit"
    >
        <template #actions>
            <!-- Action Buttons -->
            <div class="flex flex-col gap-3 pt-4 border-t border-gray-200">
                <div class="flex gap-3">
                    <UiButton
                        type="submit"
                        variant="primary"
                        :label="isEditMode ? 'Update Draft' : 'Create Draft'"
                        :loading="isLoading"
                        :disabled="isLoading"
                        class="flex-1"
                    />

                    <UiButton
                        type="button"
                        variant="secondary"
                        :label="isEditMode ? 'Update & Submit' : 'Create & Submit'"
                        :loading="isLoading"
                        :disabled="isLoading"
                        class="flex-1"
                        @click="handleSubmitAndCreate"
                    />
                </div>

                <UiButton
                    type="button"
                    variant="ghost"
                    label="Cancel"
                    :disabled="isLoading"
                    @click="handleCancel"
                />
            </div>
        </template>
    </UiFormBuilder>
</template>
